import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { FgdsProductType } from "@domain/product/product";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";

interface Section<TDevice, TPlaceholder, TConfiguration, TMapping extends ConfigProductMap> {
  devices: TDevice[];
  placeholders: TPlaceholder[];
  subtotal: number;
  mappings: TMapping[];

  getConfigurations(): TConfiguration[];

  getUniqueConfigurations(): TConfiguration[];

  getPlacedConfigurationCount(config: TConfiguration): number;
}

export class ProductSection<
  TDevice extends { config: TConfig },
  TPlaceholder extends { id: string; products: PlaceholderProduct[] },
  TConfig extends { id: string },
  TMapping extends ConfigProductMap,
> implements Section<TDevice, TPlaceholder, TConfig, TMapping> {
  private readonly ALARM_DEVICE_PLACEHOLDER_POSITION_PREFIX: string = "PA";
  private readonly GAS_WARNING_CENTER_PLACEHOLDER_POSITION_PREFIX: string = "PG";
  private readonly PLASTIC_SIGN_PLACEHOLDER_POSITION_PREFIX: string = "PK";
  private readonly SIGNAL_ELEMENT_PLACEHOLDER_POSITION_PREFIX: string = "PL";
  private readonly TRANSMITTER_PLACEHOLDER_POSITION_PREFIX: string = "PT";

  subtotal: number = 0;
  zrefSubTotal: number = 0;
  placeholderPositionPrefix: string;
  productType: FgdsProductType;
  monitoringProductType?: MonitoringCategory;

  constructor(
    public devices: TDevice[],
    public placeholders: TPlaceholder[],
    public mappings: TMapping[] = [],
    productType: FgdsProductType,
  ) {
    this.productType = productType;
    this.placeholderPositionPrefix = this.getPlaceholderPositionPrefix();
  }

  getConfigurations(): TConfig[] {
    return this.devices.map((d) => d.config);
  }

  getUniqueConfigurations(): TConfig[] {
    return Array.from(new Map(this.getConfigurations().map((c) => [c.id, c])).values());
  }

  getPlacedConfigurationCount(config: TConfig): number {
    return this.devices.filter((device) => device.config.id === config.id).length;
  }

  private getPlaceholderPositionPrefix() {
    switch (this.productType) {
      case FgdsProductType.ALARMDEVICE:
        return this.ALARM_DEVICE_PLACEHOLDER_POSITION_PREFIX;
      case FgdsProductType.GASWARNINGCENTER:
        return this.GAS_WARNING_CENTER_PLACEHOLDER_POSITION_PREFIX;
      case FgdsProductType.PLASTICSIGN:
        return this.PLASTIC_SIGN_PLACEHOLDER_POSITION_PREFIX;
      case FgdsProductType.SIGNALELEMENT:
        return this.SIGNAL_ELEMENT_PLACEHOLDER_POSITION_PREFIX;
      case FgdsProductType.TRANSMITTER:
        return this.TRANSMITTER_PLACEHOLDER_POSITION_PREFIX;
    }
    return "";
  }
}
